// /onboarding/pin — the first PIN (board 25 v2 erp/onboarding/pin--set,
// --confirm, --mismatch, --weak; copy verbatim): six dots over a hidden
// numeric input, then the same six again — two phases in one route. The
// blueprint's weak rule is checked here at the sixth digit before any round
// trip; the server re-checks (422 weak_pin shows its own sentence). Success =
// the account exists and the session is stored → the account's next screen.

import { useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Keyboard } from 'lucide-react';
import { PrimaryAction } from '@/components/Button';
import { isApiError } from '@/lib/api';
import { AuthError, AUTH_COPY, sessionStore } from '@/lib/auth';
import { useLayout } from '@/lib/layout';
import { OWNER_NAME, PIN, SHELL } from '../copy';
import { failureOf } from '../data/api';
import { nextAfterAccount, ONB } from '../data/flow';
import { Msg, PIN_LENGTH, PinDots } from '../components/widgets';
import { Wizard, type WizardError } from '../components/Wizard';
import { isWeakEnrollPin, onboardingStore, useOnboarding } from '../state';

type Phase = 'set' | 'confirm';
type SysError = { kind: 'network' | 'server'; ref: string | null; detail?: string | null };

export function PinScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const layout = useLayout();
  const mobile = layout === 'phone';
  const { intent } = useOnboarding();
  const enrollment = onboardingStore.enrollment();
  const [phase, setPhase] = useState<Phase>('set');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [weak, setWeak] = useState(false);
  const [mismatch, setMismatch] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [sysError, setSysError] = useState<SysError | null>(null);
  const finishing = useRef(false);

  if (!finishing.current && enrollment.stage() !== 'pin') {
    return <Navigate to={sessionStore.isAuthenticated() ? ONB.name : ONB.phone} replace />;
  }

  // ------------------------------------------------------------- set --

  const acceptFirst = (value: string) => {
    setMessage(null);
    if (value.length !== PIN_LENGTH) {
      setMessage(PIN.incomplete);
      return;
    }
    if (isWeakEnrollPin(value, enrollment.phone)) {
      setWeak(true);
      setMessage(PIN.weak);
      return;
    }
    setPin2('');
    setMismatch(false);
    setPhase('confirm');
  };

  // --------------------------------------------------------- confirm --

  const restart = () => {
    setPin('');
    setPin2('');
    setWeak(false);
    setMismatch(false);
    setMessage(null);
    setPhase('set');
  };

  const save = async () => {
    if (saving) return;
    setSysError(null);
    setMessage(null);
    setSaving(true);
    finishing.current = true;
    try {
      await enrollment.setPin(pin);
      navigate(await nextAfterAccount(qc), { replace: true });
    } catch (e) {
      finishing.current = false;
      if (e instanceof AuthError) {
        if (e.code === 'flow_expired' || enrollment.stage() === null) {
          navigate(ONB.phone, { replace: true, state: { notice: e.message } });
          return;
        }
        if (e.code === 'weak_pin') {
          // The server's own refusal: back to the first PIN, its dots red.
          setPin2('');
          setMismatch(false);
          setWeak(true);
          setMessage(e.message || AUTH_COPY.pinWeak);
          setPhase('set');
          return;
        }
        if (e.code === 'network') {
          setSysError({ kind: 'network', ref: null });
          return;
        }
        setPin2('');
        setMessage(e.message || AUTH_COPY.unexpected);
        return;
      }
      if (isApiError(e) && e.status > 0 && e.status < 500) {
        setPin2('');
        setMessage(e.detail ?? e.message);
        return;
      }
      setSysError({ ...failureOf(e) });
    } finally {
      setSaving(false);
    }
  };

  const acceptSecond = (value: string) => {
    setMessage(null);
    if (value.length !== PIN_LENGTH) {
      setMessage(PIN.incomplete);
      return;
    }
    if (value !== pin) {
      setMismatch(true);
      setPin2('');
      setMessage(PIN.mismatch);
      return;
    }
    void save();
  };

  const error: WizardError | null = sysError ? { kind: sysError.kind, ref: sysError.ref, detail: sysError.detail, onRetry: () => void save() } : null;
  const kbHint = !mobile && (
    <p className="onb-kb">
      <Keyboard aria-hidden="true" />
      {PIN.keyboard}
    </p>
  );

  if (phase === 'set') {
    return (
      <Wizard
        screen="pin"
        testId="onb-pin"
        state={weak ? 'weak' : 'set'}
        question={PIN.title}
        sub={PIN.sub}
        onSubmit={() => acceptFirst(pin)}
        error={error}
        peek={PIN.peekConfirm}
        panel={{ phase: 'identity', sub: 'pin' }}
        primary={
          <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile} disabled={weak} data-testid="onb-continue">
            {SHELL.continue}
          </PrimaryAction>
        }
      >
        <PinDots
          data-testid="onb-pin-input"
          label={PIN.label}
          autoFocus
          value={pin}
          invalid={weak}
          onChange={(v) => {
            setPin(v);
            if (weak || message) {
              setWeak(false);
              setMessage(null);
            }
          }}
          onComplete={acceptFirst}
        />
        {message && <Msg testId="onb-pin-error">{message}</Msg>}
        {!weak && (
          <p className="onb-rules">
            {PIN.rulesBefore}
            <bdi dir="ltr" className="ltr">
              123456
            </bdi>
            {PIN.rulesMid}
            <bdi dir="ltr" className="ltr">
              111111
            </bdi>
            {PIN.rulesAfter}
          </p>
        )}
        {!weak && kbHint}
      </Wizard>
    );
  }

  return (
    <Wizard
      screen="pin-confirm"
      testId="onb-pin"
      state={saving ? 'saving' : mismatch ? 'mismatch' : 'confirm'}
      question={PIN.confirmTitle}
      sub={PIN.confirmSub}
      onSubmit={() => acceptSecond(pin2)}
      error={error}
      peek={intent === 'owner' ? OWNER_NAME.title : undefined}
      panel={{ phase: 'identity', sub: 'pin' }}
      primary={
        <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile} loading={saving} data-testid="onb-save">
          {saving ? PIN.busy : PIN.save}
        </PrimaryAction>
      }
      tertiary={
        mismatch ? (
          <button type="button" className="onb-tert" data-testid="onb-pin-restart" onClick={restart}>
            {PIN.restart}
          </button>
        ) : undefined
      }
    >
      <PinDots
        data-testid="onb-pin-confirm-input"
        label={PIN.confirmLabel}
        autoFocus
        value={pin2}
        invalid={mismatch}
        disabled={saving}
        onChange={(v) => {
          setPin2(v);
          if (message && !mismatch) setMessage(null);
          if (mismatch && v.length > 0) {
            setMismatch(false);
            setMessage(null);
          }
        }}
        onComplete={acceptSecond}
      />
      {message && <Msg testId="onb-pin-error">{message}</Msg>}
      {!mismatch && kbHint}
    </Wizard>
  );
}
